// buildingEdges.js
//
// Генерирует вертикальные рёбра и контуры крыш для 3D-зданий (fill-extrusion),
// обходя ограничение MapLibre, что Z-координаты в линиях не поддерживаются.
//
// Пример использования:
// import { addBuildingEdges } from './buildingEdges.js';
// addBuildingEdges(map, myGeoJson);
//

import * as turf from "@turf/turf";

export function addBuildingEdges(map, geojson) {
  const roofSlabThicknessMeters = 0.2;

  function generateExtrusionPolygons(buildingsGeoJson) {
    const walls = [];
    const roofs = [];

    for (const feat of buildingsGeoJson.features || []) {
      if (!feat.geometry) continue;
      const polygons = [];
      if (feat.geometry.type === 'Polygon') {
        polygons.push(...feat.geometry.coordinates);
      } else if (feat.geometry.type === 'MultiPolygon') {
        for (const poly of feat.geometry.coordinates) polygons.push(...poly);
      } else continue;

      const height = Number(feat.properties?.height ?? 0);
      if (!(height > 0)) continue;

      for (const ring of polygons) {
        if (ring.length < 2) continue;

        for (let i = 0; i < ring.length - 1; i++) {
          const circleFeature = turf.circle(turf.point(ring[i]), roofSlabThicknessMeters, {
                        steps: 4,
                        units: 'meters'
                      }
          );

          walls.push({
                type: 'Feature',
                geometry: circleFeature.geometry,
                properties: {
                  extrusion_height: height
                }
          });
        }

       const line = turf.lineString(ring); // создаём линию по контуру крыши

       // Смещаем контур на половину толщины в обе стороны
       const outer = turf.lineOffset(line, roofSlabThicknessMeters / 2, { units: 'meters' });
       const inner = turf.lineOffset(line, -roofSlabThicknessMeters / 2, { units: 'meters' });

       // Собираем в полигон (тонкую ленту по контуру)
       const roofBand = turf.polygon([
         [
           ...outer.geometry.coordinates,
           ...inner.geometry.coordinates.reverse(),
           outer.geometry.coordinates[0]
         ]
       ]);

       roofBand.properties = {
         extrusion_base: Math.max(0, height - roofSlabThicknessMeters),
         extrusion_height: height + roofSlabThicknessMeters
       };

       roofs.push(roofBand);
      }
    }

    return {
      walls: { type: 'FeatureCollection', features: walls },
      roofs: { type: 'FeatureCollection', features: roofs }
    };
  }

  const { walls, roofs } = generateExtrusionPolygons(geojson);

  const sources = [
    ['buildingWallSource', walls],
    ['buildingRoofSlabSource', roofs]
  ];
  const layers = [
    {
      id: 'buildingWallExtrusion',
      source: 'buildingWallSource',
      color: '#ffffff',
      opacity: 1.0
    },
    {
      id: 'buildingRoofSlabExtrusion',
      source: 'buildingRoofSlabSource',
      color: '#ffffff',
      opacity: 1.0
    }
  ];

  for (const [id, data] of sources) {
    if (map.getSource(id)) map.getSource(id).setData(data);
    else map.addSource(id, { type: 'geojson', data });
  }

  for (const l of layers) {
    if (!map.getLayer(l.id)) {
      map.addLayer({
        id: l.id,
        type: 'fill-extrusion',
        source: l.source,
        paint: {
          'fill-extrusion-color': l.color,
          'fill-extrusion-height': ['get', 'extrusion_height'],
          'fill-extrusion-base': ['get', 'extrusion_base'],
          'fill-extrusion-opacity': l.opacity
        }
      });
    }
  }
}
